import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'
import {
  itemRequiresArtwork,
  itemRequiresAttachment,
  itemRequiresExternalManagement,
  itemRequiresNote,
  itemRequiresTask,
  validateOrderItem,
} from '@functions/orderItem'

import { getDeadlineStatuses } from '@functions/publicationEdition'

import useAttachments from '@hooks/useAttachments'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useOrderItemTasks from '@hooks/useOrderItemTasks'
import useSelector from '@hooks/useSelector'

import { buildCreateArtworkUrl } from '@hooks/useArtworkBuilderPaths'

import * as orderItemActions from '@redux/modules/orderItem'

import defaultFormState, { requiredFields } from '@models/orderItem'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { getCampaignPermissions, getCampaignType } from '@functions/campaign'

import type {
  AgencyModel,
  ArtworkModel,
  AttachmentModel,
  CampaignModel,
  CreateOrderItemPayload,
  OrderBundleModel,
  OrderItemModel,
  OrderItemRequestOptions,
  OrderModel,
  ProviderModel,
  ProviderPriceListItemModel,
  PublicationBookingModel,
  PublicationEditionModel,
  PublicationModel,
  ServiceModel,
  ServiceProductModel,
  StatusModel,
} from '@models/types'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

const stringifyObjectKeys: (keyof OrderItemModel)[] = ['data', 'options']

type AssignExistingArtworkParams = {
  artwork: ArtworkModel,
  dispatch: AppDispatch,
  orderItem: OrderItemModel,
  requestOptions?: OrderItemRequestOptions,
}

const assignExistingArtwork = (params: AssignExistingArtworkParams) => {
  const {
    artwork, dispatch, orderItem, requestOptions,
  } = params

  const { assignExistingArtwork: assignFn } = orderItemActions

  return dispatch(assignFn(orderItem.id, artwork.id, requestOptions))
}

type CheckForArtworkBackupParams = {
  attachments: AttachmentModel[],
  orderItem: OrderItemModel,
}

const checkForArtworkBackup = (params: CheckForArtworkBackupParams) => {
  const { attachments, orderItem: { service_product_id } } = params

  // @ts-expect-error TS2339
  const backup = attachments.find(a => a.data?.service_product_id === service_product_id)
  return !!backup
}

type CreateArtworkParams = {
  callbacks: {
    [key: string]: (payload?: {
        [key: string]: any,
    } | undefined) => void,
  },
  campaign: CampaignModel,
  dispatch: AppDispatch,
  orderItem: OrderItemModel,
  redirectUrl?: string,
}

const createArtwork = async (params: CreateArtworkParams) => {
  const {
    callbacks, campaign, dispatch, orderItem, redirectUrl,
  } = params

  const { loadCompatibleArtworks: loadFn } = orderItemActions

  // @ts-expect-error TS2345
  const result = await dispatch(loadFn(campaign.id, orderItem.service_product_id))
  if (result.success && result.data.length){
    launchModal({
      callbacks,
      modalKey: 'DuplicateOrderItemArtworkModal',
      payload: {
        existingArtworks: result.data,
        orderItem,
      },
    })

    return
  }

  const createArtworkUrl = buildCreateArtworkUrl({ orderItem, redirectUrl })
  if (createArtworkUrl){
    window.location.hash = createArtworkUrl
  }
}

type DeleteOrderItemParams = {
  dispatch: AppDispatch,
  orderItem: DeleteParams<OrderItemModel>,
}

const deleteOrderItem = (params: DeleteOrderItemParams) => {
  const { dispatch, orderItem } = params
  const { deleteOrderItem: deleteFn } = orderItemActions

  return dispatch(deleteFn(orderItem))
}

type DuplicateOrderItemParams = {
  dispatch: AppDispatch,
  orderItemParams: Partial<OrderItemModel>,
  requestOptions?: OrderItemRequestOptions,
}

const duplicateOrderItem = (params: DuplicateOrderItemParams) => {
  const { dispatch, orderItemParams, requestOptions } = params
  const { duplicateOrderItem: duplicateFn } = orderItemActions

  return dispatch(duplicateFn(orderItemParams, requestOptions))
}

type UpdateOrderItemParams = {
  dispatch: AppDispatch,
  orderItem: OrderItemModel,
  orderItemParams: Partial<OrderItemModel>,
  requestOptions?: OrderItemRequestOptions,
}

const updateOrderItem = (params: UpdateOrderItemParams) => {
  const {
    dispatch, orderItem, orderItemParams, requestOptions,
  } = params
  const { updateOrderItem: updateFn } = orderItemActions

  const updatedParams = mergeAndStringifyEntityObjects(orderItem, orderItemParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdateOrderItemOptionsParams = {
  orderItem: OrderItemModel,
  itemPayload: CreateOrderItemPayload,
  dispatch: AppDispatch,
  requestOptions?: OrderItemRequestOptions,
}

const updateOrderItemOptions = (params: UpdateOrderItemOptionsParams) => {
  const {
    dispatch, orderItem, itemPayload, requestOptions,
  } = params
  const { updateOrderItemOptions: updateFn } = orderItemActions

  const updatedParams = {
    ...itemPayload,
    options: typeof itemPayload.options === 'string' ? itemPayload.options : JSON.stringify(itemPayload.options),
  }

  return dispatch(updateFn(orderItem.id, updatedParams, requestOptions))
}

export function useRelations(orderItem: Partial<OrderItemModel> = {}) {
  const {
    artwork_id,
    order_id,
    provider_id,
    provider_price_list_item_id,
    publication_edition_id,
    publication_id,
    service_id,
    service_product_id,
    status_id,
  } = orderItem

  const {
    agencies,
    artworks,
    campaigns,
    orderBundles,
    orders,
    providerPriceListItems,
    providers,
    publicationBookings,
    publicationEditions,
    // publicationPackages,
    publications,
    serviceProducts,
    services,
    statuses,
  } = useSelector(reduxState => reduxState.entities)

  const artwork: Partial<ArtworkModel> = artwork_id && artworks[artwork_id] ? artworks[artwork_id] : {}
  const artworkStatus: Partial<StatusModel> = artwork.status_id
    && statuses[artwork.status_id] ? statuses[artwork.status_id] : {}
  const order: Partial<OrderModel> = order_id && orders[order_id] ? orders[order_id] : {}
  const agency: Partial<AgencyModel> = order.agency_id && agencies[order.agency_id] ? agencies[order.agency_id] : {}
  const campaign: Partial<CampaignModel> = order.campaign_id
    && campaigns[order.campaign_id] ? campaigns[order.campaign_id] : {}

  const orderBundle: Partial<OrderBundleModel> = order.order_bundle_id && orderBundles[order.order_bundle_id]
    ? orderBundles[order.order_bundle_id] : {}

  const orderStatus: Partial<StatusModel> = order.status_id
    && statuses[order.status_id] ? statuses[order.status_id] : {}
  const provider: Partial<ProviderModel> = provider_id && providers[provider_id] ? providers[provider_id] : {}

  const providerPriceListItem: Partial<ProviderPriceListItemModel> = provider_price_list_item_id
    && providerPriceListItems[provider_price_list_item_id]
    ? providerPriceListItems[provider_price_list_item_id] : {}

  const publication: Partial<PublicationModel> = publication_id
    && publications[publication_id] ? publications[publication_id] : {}

  const publicationBooking: Partial<PublicationBookingModel> = order.publication_booking_id
    && publicationBookings[order.publication_booking_id]
    ? publicationBookings[order.publication_booking_id] : {}
  const publicationBookingStatus: Partial<StatusModel> = publicationBooking.status_id
    && statuses[publicationBooking.status_id]
    ? statuses[publicationBooking.status_id] : {}

  const publicationEdition: Partial<PublicationEditionModel> = publication_edition_id
    && publicationEditions[publication_edition_id]
    ? publicationEditions[publication_edition_id] : {}

  const service: Partial<ServiceModel> = service_id && services[service_id] ? services[service_id] : {}

  const serviceProduct: Partial<ServiceProductModel> = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id] : {}

  const status: Partial<StatusModel> = status_id && statuses[status_id] ? statuses[status_id] : {}

  return {
    agency,
    artwork,
    artworkStatus,
    campaign,
    order,
    orderBundle,
    orderStatus,
    provider,
    providerPriceListItem,
    publication,
    publicationBooking,
    publicationBookingStatus,
    publicationEdition,
    // publicationPackage,
    service,
    serviceProduct,
    status,
  }
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useOrderItemForm(
  orderItem: Partial<OrderItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const orderItemForm = useForm(
    defaultFormState,
    { entity: orderItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [orderItem.id, orderItem.cache_key],
  )

  return {
    ...orderItemForm,
  }
}

function useOrderItem(initEntity: Partial<OrderItemModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: orderItem }: { entity: OrderItemModel } = useLatestEntity(initEntity, 'orderItems')
  const { options: orderItemOptions, publication_id, publication_edition_id } = orderItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    creating, loading, updating,
  } = useSelector(reduxState => reduxState.orderItems)

  const {
    agency,
    artwork,
    artworkStatus,
    campaign,
    order,
    orderBundle,
    orderStatus,
    provider,
    publication,
    publicationBooking,
    publicationBookingStatus,
    publicationEdition,
    // @ts-expect-error TS2339
    publicationPackage,
    providerPriceListItem,
    service,
    serviceProduct,
    status,
  } = useRelations(orderItem)

  const { currentUser } = useCurrentUser()
  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })

  // Artwork Backups
  const artworkBackupAttachmentsPayload = useAttachments({
    filters: {
      attachable_type: 'Campaign',
      attachable_id: campaign?.id,
      attachment_type: 'artwork_backup',
    },
    performHttpRequests: false,
  })

  const {
    filteredAttachments: filteredArtworkBackups,
  } = artworkBackupAttachmentsPayload

  // Artwork Pages
  // @ts-expect-error TS7034
  const filteredArtworkPages = []

  // Tasks
  const orderItemTasksPayload = useOrderItemTasks({
    filters: {
      order_item_id: orderItem.id,
    },
    performHttpRequests: false,
  })
  const { filteredOrderItemTasks } = orderItemTasksPayload

  const isPublicationOrder = order.order_type === 'publication'
  const isPaginationApproved = order.pagination_approved

  const requireQuotePermission = campaignPermissions?.orders?.require_quote !== false

  const isPending = orderStatus.key === 'pending'
  const isApproved = !isPending
  const isBooked = orderStatus.key === 'booked'

  const isEditable = isPending && (!isPublicationOrder || (!isPaginationApproved && !publicationBooking.id))
  const isDeletable = isPending
    && !isPublicationOrder
    && !order.campaign_quote_id
    && (campaignPermissions?.orders?.delete !== false)

  const isDuplicatable = isPending && !isPublicationOrder && !order.campaign_quote_id
  const isNotable = (service?.options?.notable ?? false) && provider.notes_enabled
  const isReorderable = isApproved
    && !requireQuotePermission
    && (campaignPermissions?.orders?.approve !== false)

  const isTaskable = service?.options?.taskable ?? false
  const isTemplatable = service?.options?.templatable ?? false

  const deadlineStatuses: Partial<ReturnType<typeof getDeadlineStatuses>> = isPublicationOrder
    // @ts-expect-error
    ? getDeadlineStatuses(publicationEdition) : {}

  const artworkApproved = !!artwork.id && artworkStatus.key === 'approved'
  const artworkUnapprovable = artworkApproved
    && (!publicationBooking.id || (publicationBookingStatus.key !== 'approved'))

  const artworkImageDownloadable = serviceProduct?.options?.digital?.templatable ?? false
  const artworkImposable = serviceProduct?.options?.artwork?.imposable ?? false
  const canChangeArtworkStatus = !!artwork.id
    && (isPending || isBooked)
    && campaignPermissions?.artwork?.manage !== false
  const canDownloadPrintQualityArtwork = campaignPermissions?.artwork?.print_quality !== false

  const canCreateArtwork = (isPending || isBooked)
    && isTemplatable
    && (campaignPermissions?.artwork?.manage !== false)

  const canCreateTask = !isPending && isTaskable && (campaignPermissions?.tasks?.manage !== false)

  const hasArtworkBackups = isPending
    ? checkForArtworkBackup({ attachments: filteredArtworkBackups, orderItem })
    : false
  const isBeforeBookingDeadline = deadlineStatuses?.booking?.beforeDeadline ?? false
  const isPaginated = !!publicationBooking.id
  const paginatedProofDownloadable = publicationBooking.id && artworkApproved
  const productOptionsEnabled = !isPublicationOrder
    || (publication?.options?.product_options ?? 'false') === 'true'

  const canChangeEdition = isEditable
  const canEditQuantityAndOptions = isEditable && productOptionsEnabled

  const specifiedImageIds = orderItem?.data?.specified_image_ids ?? []
  const hasSpecifiedImages = !!specifiedImageIds.length

  const tags = orderItem?.data?.tags ?? []
  const hasTags = !!tags.length

  const errors = isPending ? validateOrderItem({
    entities,
    orderItem,
    requireArtworkDataSync: agency.options?.require_artwork_data_sync,
  }) : []

  const requiredItems = {
    artwork: itemRequiresArtwork({ service, serviceProduct }),
    attachment: itemRequiresAttachment({ serviceProduct }),
    externalManagement: itemRequiresExternalManagement({ orderItem }),
    note: itemRequiresNote({ service }),
    orderItemTask: itemRequiresTask({ service }),
  }

  return {
    agency,
    artwork,
    artworkApproved,
    artworkImageDownloadable,
    artworkImposable,
    artworkStatus,
    artworkUnapprovable,
    callbacks: {
      addOrderItemArtwork: (selectedArtwork: ArtworkModel) => console.log('TODO addOrderItemArtwork', selectedArtwork),
      assignExistingArtwork: (existingArtwork: ArtworkModel, entityOptions?: OrderItemRequestOptions) => (
        assignExistingArtwork({
          artwork: existingArtwork,
          dispatch,
          orderItem,
          requestOptions: entityOptions,
        })
      ),
      bulkSelectImages: () => launchModal({
        callbacks,
        modalKey: 'BulkSelectImagesModal',
        payload: {
          callbacks: {
            updateEntity: (orderItemParams: Partial<OrderItemModel>, entityOptions?: OrderItemRequestOptions) => (
              updateOrderItem({
                orderItem, orderItemParams, dispatch, requestOptions: entityOptions,
              })
            ),
          },
          entity: orderItem,
        },
      }),
      createArtwork: (redirectUrl?: string) => (
        createArtwork({
          callbacks,
          // @ts-expect-error TS2740
          campaign,
          dispatch,
          orderItem,
          redirectUrl,
        })
      ),
      deleteOrderItem: () => deleteOrderItem({ dispatch, orderItem }),
      duplicateOrderItem: (
        orderItemParams: Partial<OrderItemModel>,
        entityOptions?: OrderItemRequestOptions,
      ) => duplicateOrderItem({
        dispatch,
        orderItemParams,
        requestOptions: entityOptions,
      }),
      editPublicationEdition: (
        params?: {
          callbacks?: {
            selectPublicationEditionId?: (payload: HandleCallbackActionParams) => void,
          },
          payload?: {
            currentEditionId?: number,
            publicationId?: number,
          },
        },
      ) => launchModal({
        callbacks,
        modalKey: 'SelectPublicationEditionModal',
        payload: {
          callbacks: params?.callbacks || {},
          currentEditionId: publication_edition_id,
          publicationId: publication_id,
          ...params?.payload,
        },
      }),
      editQuantityAndOptions: () => launchModal({
        callbacks,
        modalKey: 'EditItemOptionsModal',
        payload: {
          callbacks: {
            updateItem: (
              itemPayload: CreateOrderItemPayload,
              entityOptions?: OrderItemRequestOptions,
            ) => updateOrderItemOptions({
              dispatch,
              itemPayload,
              orderItem,
              requestOptions: entityOptions,
            }),
          },
          entity: orderItem,
        },
      }),
      manageBrandOrderArtworkModal: () => launchModal({
        callbacks,
        modalKey: 'ManageBrandOrderArtworkModal',
        payload: { orderItem },
      }),
      artworkBuilderSelectionModal: () => launchModal({
        callbacks,
        modalKey: 'ArtworkBuilderSelectionModal',
        payload: { orderItem },
      }),
      manageOrderItem: () => launchModal({
        callbacks,
        modalKey: 'ManageOrderItemModal',
        payload: { orderItem },
      }),
      manageProviderOrderItemModal: () => launchModal({
        callbacks,
        modalKey: 'ManageProviderOrderItemModal',
        payload: { orderItem },
      }),
      manageProviderOrderItemStatusModal: () => launchModal({
        callbacks,
        modalKey: 'ManageProviderOrderItemStatusModal',
        payload: { orderItem },
      }),
      manageTasks: () => launchModal({
        callbacks,
        modalKey: 'ManageOrderItemTasksModal',
        payload: {
          allowedTaskTypes: ['clean', 'move', 'other', 'remove', 'repair'],
          orderItem,
        },
      }),
      selectSpecifiedImageIds: () => console.log('TODO useOrderItem - selectSpecifiedImageIds'),
      showUploadArtworkModal: () => launchModal({
        callbacks,
        modalKey: 'UploadArtworkModal',
        payload: { orderItem },
      }),
      updateOrderItem: (orderItemParams: Partial<OrderItemModel>, entityOptions?: OrderItemRequestOptions) => (
        updateOrderItem({
          orderItem, orderItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    canChangeArtworkStatus,
    canChangeEdition,
    canCreateArtwork,
    canCreateTask,
    canDownloadPrintQualityArtwork,
    canEditQuantityAndOptions,
    creating,
    entities,
    errors,
    // @ts-expect-error TS7005
    filteredArtworkPages,
    filteredOrderItemTasks,
    hasArtworkBackups,
    hasSpecifiedImages,
    hasTags,
    isApproved,
    isBeforeBookingDeadline,
    isDeletable,
    isDuplicatable,
    isEditable,
    isNotable,
    isPaginated,
    isPending,
    isPublicationOrder,
    isReorderable,
    isTaskable,
    isTemplatable,
    loading,
    order,
    orderBundle,
    orderItem,
    orderItemOptions,
    orderStatus,
    paginatedProofDownloadable,
    providerPriceListItem,
    productOptionsEnabled,
    provider,
    publication,
    publicationBooking,
    publicationEdition,
    publicationPackage,
    requireQuotePermission,
    requiredItems,
    service,
    serviceProduct,
    specifiedImageIds,
    status,
    tags,
    updating,
  }
}

export type UseOrderItemPayload = ReturnType<typeof useOrderItem>

export default useOrderItem
